import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cambiarFuente, type FilaDeCartera } from '@/api/cartera.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useFuentes } from '@/hooks/useFuentes';
import { useToast } from '@/hooks/useToast';

/**
 * T14 · DE QUÉ BASE VIENE. `SelectorDeFuente` es el campo de siempre con las fuentes que ya
 * existen como sugerencias (una `datalist`: se elige una o se escribe otra). Lo usan importar,
 * el cliente nuevo y la ficha. Sin estado: se prueba con renderToStaticMarkup.
 */
export function SelectorDeFuente({
  valor,
  onCambiar,
  fuentes,
  etiqueta = 'Fuente',
  ayuda,
}: {
  valor: string;
  onCambiar: (valor: string) => void;
  /** Las que ya existen (`useFuentes`). */
  fuentes: readonly string[];
  etiqueta?: string;
  ayuda?: string;
}) {
  const lista = useId();
  return (
    <div>
      <Input label={etiqueta} hint={ayuda} value={valor} maxLength={60} list={lista} autoComplete="off" onChange={(e) => onCambiar(e.target.value)} className="h-11 text-base md:h-10 md:text-sm" />
      <datalist id={lista}>
        {fuentes.map((f) => (
          <option key={f} value={f} />
        ))}
      </datalist>
    </div>
  );
}

/** En la ficha del cliente: de qué base viene, y cambiarla. */
export function FuenteDelCliente({ fila }: { fila: Pick<FilaDeCartera, 'contactId' | 'fuente'> }) {
  const qc = useQueryClient();
  const toast = useToast();
  const fuentes = useFuentes();
  const [valor, setValor] = useState(fila.fuente);
  const cambiada = valor.trim() !== '' && valor.trim() !== fila.fuente;
  const guardar = useMutation({
    mutationFn: () => cambiarFuente([fila.contactId], valor),
    onSuccess: ({ fuente }) => {
      setValor(fuente);
      toast.success(`Ahora viene de «${fuente}».`);
      void qc.invalidateQueries({ queryKey: ['cartera'] });
    },
    onError: (e: Error) => toast.error('No se pudo cambiar la fuente', e.message),
  });
  return (
    <form
      className="flex flex-wrap items-end gap-2 rounded-xl border border-surface-border p-3 dark:border-slate-800"
      onSubmit={(e) => {
        e.preventDefault();
        if (cambiada) guardar.mutate();
      }}
    >
      <div className="min-w-[12rem] flex-1">
        <SelectorDeFuente valor={valor} onCambiar={setValor} fuentes={fuentes} etiqueta="Fuente: de qué base viene" />
      </div>
      <Button type="submit" variant="outline" className="min-h-[44px] md:min-h-0" disabled={!cambiada} loading={guardar.isPending}>
        Cambiar fuente
      </Button>
    </form>
  );
}
